import { createFileRoute, Link } from "@tanstack/react-router";
import { GraduationCap, BookOpen, Heart, User, Lightbulb } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SiteHeader } from "@/components/SiteHeader";

export const Route = createFileRoute("/about")({
  head: () => ({
    meta: [
      { title: "About SmartEssay — Built by a 27-year English teacher" },
      { name: "description", content: "Built by Nicole, a 27-year public-school English teacher with a Master's in Education, to scaffold student writing without replacing student thinking." },
      { property: "og:title", content: "About SmartEssay — Built by a 27-year English teacher" },
      { property: "og:description", content: "The teacher behind SmartEssay and why this tool exists." },
      { property: "og:url", content: "https://smartessay.ai/about" },
      { property: "og:type", content: "profile" },
      { name: "twitter:title", content: "About SmartEssay" },
      { name: "twitter:description", content: "Built by a 27-year English teacher to scaffold student writing." },
    ],
    links: [{ rel: "canonical", href: "https://smartessay.ai/about" }],
    scripts: [{
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "AboutPage",
        url: "https://smartessay.ai/about",
        mainEntity: {
          "@type": "Person",
          name: "Nicole",
          jobTitle: "English Teacher & Founder",
          description: "27-year public-school English teacher with a Master's in Education and founder of SmartEssay.",
          worksFor: { "@type": "Organization", name: "SmartEssay", url: "https://smartessay.ai" },
        },
      }),
    }],
  }),
  component: AboutPage,
});

function AboutPage() {
  return (
    <div className="min-h-screen bg-hero-gradient">
      <SiteHeader />

      <main className="mx-auto max-w-3xl px-6 pb-24">
        <div className="text-center">
          <p className="text-xs font-medium uppercase tracking-[0.22em] text-primary">The teacher</p>
          <div className="mx-auto mt-3 h-px w-16 bg-gold/70" aria-hidden />
          <div className="mx-auto mt-5 flex h-14 w-14 items-center justify-center rounded-full border border-gold/50 bg-card text-primary">
            <User className="h-7 w-7" />
          </div>
          <h1 className="mt-5 font-display text-4xl font-medium leading-tight tracking-tight sm:text-5xl">
            About <em>me</em>
          </h1>
          <p className="mx-auto mt-3 max-w-lg text-muted-foreground">
            The teacher behind SmartEssay.
          </p>
        </div>

        <div className="mt-12 space-y-8">
          <div className="flex gap-4">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-semibold text-primary-foreground">
              <BookOpen className="h-4 w-4" />
            </div>
            <div>
              <h2 className="font-display text-lg font-semibold">27 years in the classroom</h2>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                I have spent the last 27 years teaching K–12 Public Education in Secondary English. Every day, I watched students struggle not with ideas, but with structure — how to turn their thoughts into a coherent essay that teachers could follow and grade fairly.
              </p>
            </div>
          </div>

          <div className="flex gap-4">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-semibold text-primary-foreground">
              <GraduationCap className="h-4 w-4" />
            </div>
            <div>
              <h2 className="font-display text-lg font-semibold">Master's in Education</h2>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                My Master's in Education deepened my understanding of how students learn to write. Research consistently shows that scaffolding — giving students a frame to fill in — is one of the most effective ways to build confident, independent writers.
              </p>
            </div>
          </div>

          <div className="flex gap-4">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-semibold text-primary-foreground">
              <Heart className="h-4 w-4" />
            </div>
            <div>
              <h2 className="font-display text-lg font-semibold">Why I built SmartEssay</h2>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                I built this site to help students understand the basic structure of an essay while still being able to use their own ideas. SmartEssay is not about doing the thinking for them — it is about giving them a solid scaffold so their thinking can shine through. The frames, quotes, and outlines are starting points. The analysis, the connections, and the voice are all theirs.
              </p>
            </div>
          </div>

          <div className="flex gap-4">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-semibold text-primary-foreground">
              <Lightbulb className="h-4 w-4" />
            </div>
            <div>
              <h2 className="font-display text-lg font-semibold">What AI alone cannot do</h2>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                Over the years, I have watched AI tools like ChatGPT fall short of expectations in the classroom. They can generate text, but they do not teach students how to generate their own ideas, find appropriate support information, or design their papers in an organized and logical manner. SmartEssay solves that problem by giving students a clear framework to think within — so they learn the process, not just the product.
              </p>
            </div>
          </div>
        </div>

        <div className="mt-12 rounded-2xl border border-border bg-card p-8 text-center shadow-soft">
          <h2 className="font-display text-lg font-semibold">Want to get in touch?</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            I would love to hear from students, parents, and fellow educators.
          </p>
          <div className="mt-5 flex flex-wrap justify-center gap-3">
            <Button asChild className="shadow-glow">
              <Link to="/contact">Contact me</Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/help">Help Center</Link>
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
}
